/**
 * FeedbackSection - Help and feedback settings
 */

import { Button } from '@/components/ui/button'
import { GlassCard, GlassCardContent, GlassCardDescription, GlassCardHeader, GlassCardTitle } from '@/components/ui/glass-card'
import { Bug, ExternalLink, Lightbulb, MessageSquarePlus, Star } from 'lucide-react'

interface FeedbackCardProps {
  icon: React.ReactNode
  iconBg: string
  title: string
  description: string
  buttonText: string
  buttonIcon?: React.ReactNode
  onClick: () => void
}

function FeedbackCard({ icon, iconBg, title, description, buttonText, buttonIcon, onClick }: FeedbackCardProps) {
  return (
    <GlassCard>
      <GlassCardHeader>
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${iconBg}`}>
            {icon}
          </div>
          <div>
            <GlassCardTitle className="text-lg">{title}</GlassCardTitle>
            <GlassCardDescription>{description}</GlassCardDescription>
          </div>
        </div>
      </GlassCardHeader>
      <GlassCardContent>
        <Button
          variant="outline"
          size="sm"
          onClick={onClick}
          className="w-full sm:w-auto"
        >
          {buttonIcon || <ExternalLink className="h-4 w-4 mr-2" />}
          {buttonText}
        </Button>
      </GlassCardContent>
    </GlassCard>
  )
}

export function FeedbackSection() {
  return (
    <div className="space-y-6">
      {/* Main feedback card */}
      <FeedbackCard
        icon={<MessageSquarePlus className="h-5 w-5 text-purple-400" />}
        iconBg="bg-purple-500/20"
        title="Feature Requests"
        description="Submit ideas and vote on features you'd like to see"
        buttonText="Open Feedback Board"
        onClick={() => window.open('https://ai-edge.canny.io/feedback-and-suggestions', '_blank')}
      />

      {/* Additional feedback options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <GlassCard>
          <GlassCardContent className="py-5">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-red-500/20">
                <Bug className="h-4 w-4 text-red-400" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-white text-sm">Report a Bug</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  Found something broken? Let us know.
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-3 h-8 px-3 text-xs"
                  onClick={() => window.open('https://ai-edge.canny.io/feedback-and-suggestions', '_blank')}
                >
                  Report Issue
                  <ExternalLink className="h-3 w-3 ml-1.5" />
                </Button>
              </div>
            </div>
          </GlassCardContent>
        </GlassCard>

        <GlassCard>
          <GlassCardContent className="py-5">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-amber-500/20">
                <Lightbulb className="h-4 w-4 text-amber-400" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-white text-sm">Documentation</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  Learn how to get the most from the optimizer.
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-3 h-8 px-3 text-xs"
                  onClick={() => window.open('https://docs.ai-edge.io', '_blank')}
                >
                  View Docs
                  <ExternalLink className="h-3 w-3 ml-1.5" />
                </Button>
              </div>
            </div>
          </GlassCardContent>
        </GlassCard>
      </div>

      {/* Rating prompt */}
      <GlassCard className="border-green-500/20 bg-gradient-to-br from-green-500/5 to-emerald-500/5">
        <GlassCardContent className="py-5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/20">
                <Star className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <h4 className="font-semibold text-green-200">Enjoying the Optimizer?</h4>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Your feedback helps us prioritize improvements.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 border-green-500/30 text-green-300 hover:bg-green-500/10"
              onClick={() => window.open('https://ai-edge.canny.io/feedback-and-suggestions', '_blank')}
            >
              Leave Feedback
            </Button>
          </div>
        </GlassCardContent>
      </GlassCard>
    </div>
  )
}
